import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { removeCoUsers } from 'entities/CoUser/api';
import { CoUser } from 'entities/CoUser/types';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { FeaturesEnum } from 'entities/Company/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { EmptyTable } from 'shared/ui/EmptyTable';
import { getFilteredArray } from 'shared/helpers/getFilteredArray';
import { useDebounce } from 'shared/helpers/useDebounce';
import { useSearchParam } from 'shared/helpers/useSearchParam';
import { SearchInput } from 'shared/ui/SearchInput';
import { TableWithHeader } from 'shared/ui/tables/TableWithHeader';
import { CoUsersDashboardColumns } from 'widgets/CoUsers/ui/CoUsersDashboard/consts/DashboardColumns';
import { RemoveCoUserModal } from 'widgets/CoUsers/ui/RemoveCoUserModal';
import { useSubscriptionStoreStore } from 'widgets/Subscriptions/model/state';
import { useAuthStore } from 'processes/auth/model/state';
import { getMaxValue } from 'shared/helpers/getMaxValue';
import { useFeature } from 'shared/hooks/useFeatures';
import { usePermissions } from 'shared/hooks/usePermissions';
import CoUserFilters from '../CoUserFilters';
import Chip from 'shared/ui/Chip';
import { useCoUsersStore } from 'store/co-users';

interface Props {
	coUsers?: Array<CoUser>;
}

const defaultSearch = '';

export const CoUsersDashboard: FunctionComponent<Props> = ({ coUsers }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuthStore();
	const { canDeleteCoUsers } = usePermissions();
	const { coUsersFilters, setCoUsersFilters } = useCoUsersStore();
	const { getFeature } = useFeature();
	const { currentSubscription } = useSubscriptionStoreStore();

	const [searchParam, setSearchParam] = useSearchParam('search', defaultSearch);

	const initialSearchParam = useMemo(() => searchParam ?? defaultSearch, [searchParam]);
	const [searchInputValue, setSearchInputValue] = useState<string>(initialSearchParam);
	const debouncedSearchInputValue = useDebounce<string>(searchInputValue, 500);

	const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchInputValue(event.target.value);
	};

	const selectedTableFilters = useMemo(() => {
		if (coUsersFilters) {
			const { from, to, ...rest } = coUsersFilters;
			return {
				date:
					from && to
						? `${format(new Date(from), 'dd/MM/yyy')} - ${format(new Date(to), 'dd/MM/yyy')}`
						: undefined,
				...rest,
			};
		}
		return [];
	}, [coUsersFilters]);

	useEffect(() => {
		setSearchParam(debouncedSearchInputValue);
	}, [debouncedSearchInputValue]);

	// CoUser removing flow
	const [coUserForRemove, setCoUserForRemove] = useState<CoUser | undefined>(undefined);
	const handleOpenModal = (coUser: CoUser) => setCoUserForRemove(coUser);
	const handleCloseModal = () => setCoUserForRemove(undefined);

	const queryClient = useQueryClient();
	const { mutate } = useMutation({
		mutationKey: ['co-users', coUserForRemove?._id],
		mutationFn: removeCoUsers,
	});

	const handleRemoveCoUser = useCallback(() => {
		mutate(coUserForRemove?._id, {
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: ['co-users'] });
				enqueueSnackbar({
					message: t('co_users.removed_message', {
						value: coUserForRemove?.userId?.email ?? coUserForRemove?.email,
					}),
					variant: 'success',
				});
				handleCloseModal();
			},
			onError: (err) => {
				showErrorNotification(
					err as AxiosError,
					t('co_users.error_while_removing', {
						value: coUserForRemove?.userId?.email ?? coUserForRemove?.email,
					}),
				);
			},
		});
	}, [coUserForRemove, queryClient]);

	// Prepare CoUsers data for dashboard
	const preparedCoUsers = useMemo(() => {
		if (!coUsers) return [];
		return coUsers.map((coUser) => ({
			...coUser,
			openModal: (coUser: CoUser) => handleOpenModal(coUser),
			navigateToDetails: (coUser: CoUser) => {
				navigate(`/co-users/${coUser._id}`);
			},
		}));
	}, [coUsers]);

	const filteredCoUsers = useMemo(() => {
		if (!coUsers) return [];
		if (!searchParam) return preparedCoUsers;

		return getFilteredArray(
			preparedCoUsers,
			['userId?.email', 'email', 'userId.firstName', 'userId.lastName'],
			searchParam,
		);
	}, [preparedCoUsers, searchParam]);

	const rowsData = useMemo(() => {
		return filteredCoUsers.map((coUser) => ({
			...coUser,
			id: coUser._id,
			authUserId: user?._id,
			canDelete: canDeleteCoUsers,
			isDealer: getFeature(FeaturesEnum.RESELLER)?.isEnabled,
		}));
	}, [filteredCoUsers, canDeleteCoUsers]);

	return (
		<>
			<RemoveCoUserModal
				coUser={coUserForRemove}
				open={!!coUserForRemove}
				handleClose={handleCloseModal}
				handleRemoveCoUser={handleRemoveCoUser}
			/>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					marginBottom: '34px',
				}}
			>
				<Typography
					variant={'h5'}
					color={ColorPalette.astronaut}
					sx={{
						marginRight: 'auto',
					}}
				>
					{t('co_users.your_co_users')} ({filteredCoUsers?.length}/
					{getMaxValue(
						currentSubscription?.maxCousers || 0,
						currentSubscription?.maxFreeCousers || 0,
					)}
					)
				</Typography>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: 2,
					}}
				>
					<SearchInput
						value={searchInputValue}
						onChange={handleSearchInputChange}
						disabled={!coUsers?.length}
					/>

					<CoUserFilters />
				</Box>
			</Box>
			<Box sx={{ display: 'flex', gap: 1, marginBottom: 1 }}>
				{Object.entries(selectedTableFilters).map(
					([key, value]) =>
						value && (
							<Chip
								key={key}
								label={value as string}
								onDelete={() => {
									if (key === 'date') {
										setCoUsersFilters({
											...coUsersFilters,
											from: undefined,
											to: undefined,
										});
									} else {
										setCoUsersFilters({ ...coUsersFilters, [key]: undefined });
									}
								}}
								color="secondary"
								size="small"
							/>
						),
				)}
			</Box>
			{coUsers?.length ? (
				<TableWithHeader columns={CoUsersDashboardColumns} rows={rowsData} />
			) : (
				<EmptyTable text={t('co_users.didnt_invite_yet')} />
			)}
		</>
	);
};
